import { useEffect, useState, useRef } from 'react';
import { MapContainer, GeoJSON, Marker, useMap } from 'react-leaflet';
import L, { divIcon, LatLngBounds } from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface Props {
  lat: number;
  lon: number;
  country: string;
}

// Named bounding boxes for countries that need a custom viewport.
// Modelled after CONUS_BOUNDS — each entry pins the map to a culturally
// meaningful core region rather than fitting all (possibly distant) polygons.
const CONUS_BOUNDS = new LatLngBounds([24.396308, -125.0], [49.384358, -66.93457]);

const COUNTRY_BOUNDS: Record<string, LatLngBounds> = {
  // Metropolitan France + Corsica; excludes overseas territories
  FR: new LatLngBounds([41.3, -5.2], [51.2, 10.0]),
  // NZ North + South Islands + Stewart Island; excludes Chathams and Pacific dependencies
  NZ: new LatLngBounds([-47.5, 165.8], [-34.0, 178.8]),
  // Germany mainland + Baltic islands
  DE: new LatLngBounds([47.2, 5.8], [55.2, 15.1]),
  // Japan home islands (Kyushu, Shikoku, Honshu, Hokkaido); excludes Okinawa chain
  JP: new LatLngBounds([30.8, 129.5], [45.6, 146.0]),
  // Spain mainland + Balearic Islands; excludes Canary Islands
  ES: new LatLngBounds([35.8, -9.4], [43.9, 4.4]),
  // Australia mainland + Tasmania
  AU: new LatLngBounds([-43.7, 113.0], [-10.6, 153.7]),
  // China mainland
  CN: new LatLngBounds([20.0, 73.5], [53.7, 135.0]),
  // India mainland + Andaman & Nicobar Islands
  IN: new LatLngBounds([6.6, 68.0], [35.6, 97.5]),
  // Great Britain + Northern Ireland; excludes Channel Islands and Isle of Man
  GB: new LatLngBounds([49.8, -8.2], [60.9, 1.8]),
};

// Countries whose GeoJSON feature has iso2="-99" (no valid ISO in the dataset).
// Keyed by the name stored in the dataset, value is the correct iso2.
const NAME_TO_ISO2: Record<string, string> = {
  france: 'FR',
  norway: 'NO',
  kosovo: 'XK',
};

// Common country code aliases not in ISO-3166-1 alpha-2
const ALIASES: Record<string, string> = {
  UK: 'GB',
  ENG: 'GB',
  SCO: 'GB',
  WAL: 'GB',
  'GREAT BRITAIN': 'GB',
  'ENGLAND': 'GB',
  'SCOTLAND': 'GB',
  'WALES': 'GB',
  'NORTHERN IRELAND': 'GB',
};

// Module-level cache — fetched once, reused across all instances
let worldData: GeoJSON.FeatureCollection | null = null;
let worldDataPromise: Promise<GeoJSON.FeatureCollection> | null = null;

function loadWorldData(): Promise<GeoJSON.FeatureCollection> {
  if (worldData) return Promise.resolve(worldData);
  if (worldDataPromise) return worldDataPromise;
  worldDataPromise = fetch('/world-countries.json')
    .then(r => r.json())
    .then(d => { worldData = d; return d; });
  return worldDataPromise;
}

function polyBbox(coords: number[][][][]): { minLon: number; maxLon: number; minLat: number; maxLat: number } {
  const ring = coords[0];
  let minLon = Infinity, maxLon = -Infinity, minLat = Infinity, maxLat = -Infinity;
  for (const c of ring) {
    if (c[0] < minLon) minLon = c[0];
    if (c[0] > maxLon) maxLon = c[0];
    if (c[1] < minLat) minLat = c[1];
    if (c[1] > maxLat) maxLat = c[1];
  }
  return { minLon, maxLon, minLat, maxLat };
}

function isConusPoly(coords: number[][][][]): boolean {
  const { minLon, maxLon, minLat, maxLat } = polyBbox(coords);
  return minLon >= -125 && maxLon <= -60 && minLat >= 24 && maxLat <= 50;
}

function filterUSPolygons(
  feature: GeoJSON.Feature,
  lat: number,
  lon: number,
  conusOnly: boolean,
): GeoJSON.Feature {
  if (feature.geometry.type !== 'MultiPolygon') return feature;
  const polys = feature.geometry.coordinates as number[][][][];

  if (conusOnly) {
    const filtered = polys.filter(isConusPoly);
    return { ...feature, geometry: { type: 'MultiPolygon', coordinates: filtered.length ? filtered : polys } };
  }

  // For non-CONUS US (Alaska, Hawaii, territories): keep the polygon closest to the pin.
  const nonConus = polys.filter(p => !isConusPoly(p));
  if (!nonConus.length) return feature;

  let best = nonConus[0];
  let bestDist = Infinity;
  for (const poly of nonConus) {
    const { minLon, maxLon, minLat, maxLat } = polyBbox(poly);
    if (lat >= minLat && lat <= maxLat && lon >= minLon && lon <= maxLon) {
      best = poly;
      bestDist = -1;
      break;
    }
    const cLat = (minLat + maxLat) / 2;
    const cLon = (minLon + maxLon) / 2;
    const dist = (lat - cLat) ** 2 + (lon - cLon) ** 2;
    if (dist < bestDist) { bestDist = dist; best = poly; }
  }
  return { ...feature, geometry: { type: 'MultiPolygon', coordinates: [best] } };
}

// Look up a feature by iso2, with fallback to NAME_TO_ISO2 for countries that
// have iso2="-99" in the dataset (France, Norway, Kosovo, etc.).
function findFeatureByIso2(world: GeoJSON.FeatureCollection, iso2: string): GeoJSON.Feature | undefined {
  const upper = iso2.toUpperCase();

  // Normal lookup
  const direct = world.features.find(
    f => (f as GeoJSON.Feature).properties?.iso2?.toUpperCase() === upper
  ) as GeoJSON.Feature | undefined;
  if (direct) return direct;

  // Reverse-map iso2 → dataset name for bad-iso2 entries
  const nameEntry = Object.entries(NAME_TO_ISO2).find(([, v]) => v === upper);
  if (nameEntry) {
    const [name] = nameEntry;
    return world.features.find(
      f => (f as GeoJSON.Feature).properties?.name?.toLowerCase() === name
    ) as GeoJSON.Feature | undefined;
  }

  return undefined;
}

function getCountryFeature(
  world: GeoJSON.FeatureCollection,
  iso2: string,
  lat?: number,
  lon?: number,
  conusOnly?: boolean,
): GeoJSON.FeatureCollection | null {
  const feature = findFeatureByIso2(world, iso2);
  if (!feature) return null;

  if (iso2.toUpperCase() === 'US' && lat != null && lon != null) {
    const filtered = filterUSPolygons(feature, lat, lon, conusOnly ?? false);
    return { type: 'FeatureCollection', features: [filtered] };
  }

  return { type: 'FeatureCollection', features: [feature] };
}

async function resolveCountryCode(country: string): Promise<string | null> {
  const trimmed = country.trim();
  if (!trimmed) return null;

  const upper = trimmed.toUpperCase();

  if (ALIASES[upper]) return ALIASES[upper];

  // Already a valid 2-letter ISO code
  if (/^[A-Z]{2}$/.test(upper)) return upper;

  const world = await loadWorldData();

  // Match iso3 (e.g. "USA", "GBR", "NZL")
  if (/^[A-Z]{3}$/.test(upper)) {
    const byIso3 = world.features.find(
      f => ((f as GeoJSON.Feature).properties?.iso3 as string | undefined)?.toUpperCase() === upper
    );
    if (byIso3) return ((byIso3 as GeoJSON.Feature).properties?.iso2 as string) ?? null;
  }

  // Full name match (case-insensitive)
  const lower = trimmed.toLowerCase();

  // Check NAME_TO_ISO2 first for countries with bad iso codes in the dataset
  if (NAME_TO_ISO2[lower]) return NAME_TO_ISO2[lower];

  const byName = world.features.find(
    f => ((f as GeoJSON.Feature).properties?.name as string | undefined)?.toLowerCase() === lower
  );
  if (byName) {
    const iso2 = (byName as GeoJSON.Feature).properties?.iso2 as string;
    // If the dataset has a bad iso2, check our override map
    if (iso2 && iso2 !== '-99') return iso2;
    if (NAME_TO_ISO2[lower]) return NAME_TO_ISO2[lower];
  }

  // Prefix match
  const byPartial = world.features.find(
    f => ((f as GeoJSON.Feature).properties?.name as string | undefined)?.toLowerCase().startsWith(lower)
  );
  if (byPartial) {
    const iso2 = (byPartial as GeoJSON.Feature).properties?.iso2 as string;
    const name = ((byPartial as GeoJSON.Feature).properties?.name as string | undefined)?.toLowerCase() ?? '';
    if (iso2 && iso2 !== '-99') return iso2;
    if (NAME_TO_ISO2[name]) return NAME_TO_ISO2[name];
  }

  // Network fallback
  try {
    const res = await fetch(
      `https://restcountries.com/v3.1/name/${encodeURIComponent(trimmed)}?fields=cca2`,
      { signal: AbortSignal.timeout(5000) }
    );
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data) && data[0]?.cca2) return data[0].cca2 as string;
    }
  } catch {
    // ignore
  }
  return null;
}

function isInConus(lat: number, lon: number): boolean {
  return CONUS_BOUNDS.contains([lat, lon]);
}

function isUSCountry(country: string | undefined | null): boolean {
  if (!country) return false;
  const u = country.trim().toUpperCase();
  return u === 'US' || u === 'USA' || u.startsWith('UNITED STATES');
}

function MapFitter({
  lat,
  lon,
  geojson,
  countryBounds,
  conusOnly,
}: {
  lat: number;
  lon: number;
  geojson: GeoJSON.FeatureCollection | null;
  countryBounds: LatLngBounds | null;
  conusOnly: boolean;
}) {
  const map = useMap();

  useEffect(() => {
    if (conusOnly) {
      map.fitBounds(CONUS_BOUNDS, { padding: [24, 24], animate: false });
      return;
    }
    if (countryBounds) {
      // Expand bounds by ~15% so fitBounds picks a zoom level that keeps the
      // full country visible inside the 180px-tall container.
      const sw = countryBounds.getSouthWest();
      const ne = countryBounds.getNorthEast();
      const latPad = (ne.lat - sw.lat) * 0.15;
      const lonPad = (ne.lng - sw.lng) * 0.15;
      const padded = new L.LatLngBounds(
        [sw.lat - latPad, sw.lng - lonPad],
        [ne.lat + latPad, ne.lng + lonPad],
      );
      map.fitBounds(padded, { animate: false });
      return;
    }
    // Default: center on pin at a fixed zoom. The world context layer provides
    // geographic reference so the user always knows where they are.
    void geojson; // geojson is rendered separately; we don't fit to it here
    map.setView([lat, lon], 5, { animate: false });
  }, [lat, lon, geojson, countryBounds, conusOnly, map]);

  return null;
}

const pinIcon = divIcon({
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="28" viewBox="0 0 22 28" fill="none">
    <path d="M11 0C4.925 0 0 4.925 0 11c0 7.5 11 17 11 17s11-9.5 11-17c0-6.075-4.925-11-11-11z" fill="#0175C2"/>
    <circle cx="11" cy="11" r="4.5" fill="white"/>
  </svg>`,
  className: '',
  iconSize: [22, 28],
  iconAnchor: [11, 28],
});

export default function LocationPreview({ lat, lon, country }: Props) {
  const [countryGeoJSON, setCountryGeoJSON] = useState<GeoJSON.FeatureCollection | null>(null);
  const [worldGeoJSON, setWorldGeoJSON] = useState<GeoJSON.FeatureCollection | null>(null);
  const [resolvedIso2, setResolvedIso2] = useState<string | null>(null);
  const geojsonKey = useRef(0);
  const resolvedFor = useRef<string | null>(null);

  if (lat == null || lon == null) return null;
  const conusOnly = isUSCountry(country) && isInConus(lat, lon);

  // Load world data for the faded context layer once on mount
  useEffect(() => {
    loadWorldData().then(d => setWorldGeoJSON(d));
  }, []);

  // Re-resolve whenever country changes, or when lat/lon changes for US
  const resolveKey = `${country}|${lat.toFixed(2)}|${lon.toFixed(2)}`;

  useEffect(() => {
    if (!country) return;
    if (resolveKey === resolvedFor.current) return;

    let cancelled = false;
    (async () => {
      const code = await resolveCountryCode(country);
      if (cancelled || !code) return;
      const world = await loadWorldData();
      if (cancelled) return;
      const feature = getCountryFeature(world, code, lat, lon, isUSCountry(country) && isInConus(lat, lon));
      resolvedFor.current = resolveKey;
      geojsonKey.current += 1;
      setResolvedIso2(code);
      setCountryGeoJSON(feature);
    })();

    return () => { cancelled = true; };
  }, [resolveKey, country, lat, lon]);

  // Use the async-resolved iso2 for bounds lookup so full country names like
  // "Germany" resolve correctly (the sync fallback only handles 2-letter codes).
  let countryBounds: LatLngBounds | null = null;
  if (!conusOnly && resolvedIso2) {
    const bounds = COUNTRY_BOUNDS[resolvedIso2];
    if (bounds && bounds.contains([lat, lon])) {
      countryBounds = bounds;
    }
  }

  return (
    <div
      className="relative mt-2 rounded-lg overflow-hidden border border-bolt-border"
      style={{ height: 180 }}
    >
      <div className="absolute inset-0" style={{ background: '#0B1929' }} />

      <MapContainer
        key={`${lat.toFixed(4)},${lon.toFixed(4)}`}
        center={[lat, lon]}
        zoom={5}
        style={{ height: '100%', width: '100%', background: 'transparent' }}
        zoomControl={false}
        attributionControl={false}
        dragging={false}
        scrollWheelZoom={false}
        doubleClickZoom={false}
        touchZoom={false}
        keyboard={false}
      >
        <MapFitter lat={lat} lon={lon} geojson={countryGeoJSON} countryBounds={countryBounds} conusOnly={conusOnly} />

        {/* Faded world context layer */}
        {worldGeoJSON && (
          <GeoJSON
            key="world"
            data={worldGeoJSON}
            style={{
              fillColor: '#1A2E45',
              fillOpacity: 0.35,
              color: '#1E3A5F',
              weight: 0.5,
              opacity: 0.5,
            }}
          />
        )}

        {/* Active country highlight */}
        {countryGeoJSON && (
          <GeoJSON
            key={geojsonKey.current}
            data={countryGeoJSON}
            style={{
              fillColor: '#1E3A5F',
              fillOpacity: 0.9,
              color: '#2D6A9F',
              weight: 1.5,
              opacity: 1,
            }}
          />
        )}

        <Marker position={[lat, lon]} icon={pinIcon} />
      </MapContainer>

      <div className="absolute bottom-2 left-3 pointer-events-none">
        <span className="text-xs font-medium text-white/60 bg-black/40 px-2 py-0.5 rounded-full backdrop-blur-sm tabular-nums">
          {lat.toFixed(4)}, {lon.toFixed(4)}
        </span>
      </div>
    </div>
  );
}
